import { Text } from "@chakra-ui/react";
import { type ReactElement } from "react";
import { Trans, useTranslation } from "react-i18next";
import { type BuyerBidDetailDto } from "../../../autogen/bff-api";
import { useConfirmationModal } from "../../../common/useConfirmationModal";
import { verdictReopensDecision, type BidDecision } from "./decidedBidEdit";
import { useBidQualificationDecision } from "./useBidQualificationDecision";
import { useUndoRejectionDialog } from "./useUndoRejectionDialog";

export interface StandingVerdictGuard {
  changeVerdict: (verdict: boolean | null, applyToReview: () => void) => void;
  modal: ReactElement;
}

const ReopenWarning = ({ standingVerdict }: { standingVerdict: BidDecision }) => {
  const { t } = useTranslation();
  return (
    <Text>
      <Trans
        i18nKey="The qualification of this supplier is <bold>{{state}}</bold>. If you change the answer, the status goes back to not assessed. Do you want to continue?"
        values={{ state: standingVerdict === "REJECTED" ? t("Rejected") : t("Qualified") }}
        components={{ bold: <Text as="span" fontWeight="bold" /> }}
      />
    </Text>
  );
};

export const useStandingVerdictGuard = (detail: BuyerBidDetailDto): StandingVerdictGuard => {
  const { t } = useTranslation();
  const { show, modal } = useConfirmationModal();
  const qualification = useBidQualificationDecision(detail);
  const rejection = useUndoRejectionDialog(detail);

  const standingVerdict: BidDecision = rejection.isRejected ? "REJECTED" : qualification.decision ? "QUALIFIED" : null;


  const changeVerdict = (verdict: boolean | null, applyToReview: () => void) => {
    if (!verdictReopensDecision(standingVerdict, verdict)) {
      applyToReview();
      return;
    }
    if (rejection.isRejected) {
      rejection.open(applyToReview);
      return;
    }

    show({
      title: String(t("Reopen the decision?")),
      description: <ReopenWarning standingVerdict={standingVerdict} />,
      confirmButtonText: String(t("Yes")),
      cancelButtonText: String(t("No")),
      confirmButtonColor: "red",
      size: "lg",
      onConfirmed: async () => {
        if (await qualification.decide(false)) applyToReview();
      },
    });
  };

  return {
    changeVerdict,
    modal: (
      <>
        {modal}
        {rejection.modal}
      </>
    ),
  };
};
